import { listarServicosDigitais, listarAparelhos, montarComplementosProposta, formatarTelefoneExibicao, NOMES_ADICIONAIS_MOVEL } from '../whatsapp/complementos-proposta';
export { listarServicosDigitais, listarAparelhos, marcaDaProposta, promocaoDoPlano };
import { gerarMensagemPropostaCompleta as gerarPropostaCanonica, megabonusDaProposta, operacoesDaProposta, ehLinhaDeVoz, descricaoFibraDaProposta, marcaDaProposta, promocaoDoPlano, descricaoQuantidadesProposta, ehPropostaCpf, itensDaPropostaCpf, primeiroNome } from '../whatsapp/gerar-mensagem-proposta';
/**
 * Catálogo de Planos Claro CNPJ
 * Atualizado em: Janeiro 2026
 *
 * REGRAS DE NEGÓCIO:
 * - Dados são COMPARTILHADOS entre linhas (3 linhas x 40GB = 120GB compartilhados)
 * - TT = Transferência de Titularidade (CPF Claro → CNPJ Claro)
 * - PORTABILIDADE = Outra operadora (Vivo, Tim, etc.) → Claro
 * - BLOCO DE DADOS = Linha adicional para aumentar pacote de dados
 * - Megabônus ÚNICO se combinar com Claro Fibra (não soma por linha); o número é megaBonusGb(), do banco
 */

export interface PlanoCNPJ {
  tipoPessoa?: 'PF' | 'PJ';
  id: string;
  nome: string;
  gb: number;
  valor: number;
  beneficios: string[];
  tipo: 'MOVEL' | 'COMBO' | 'FIBRA' | 'RESIDENCIAL';
  megabonus?: boolean | number; // recebe o Mega Bônus com fibra; o número é megaBonusGb() (composições antigas guardam o GB)
  destaque?: boolean; // Para planos recomendados
  passaporte?: boolean; // Claro Passaporte incluído
  velocidade?: string; // Para planos de fibra
  maxLinhasBan?: number; // Máximo de linhas por BAN (apenas Bloco de Dados)
  produtoPedidoId?: string | null; // Serviços/TI: produto do pedido que ESTA opção gera (override por opção na conversão)
  categoria?: string; // categoria do catalogo de onde a opcao veio (o codigo sozinho e ambiguo)
  mesesPromocionais?: number | null;
  valorAposPromocao?: number | null;
  praca?: string | null; // CPF: 'GM' ou 'MD'. Só indica de qual praça é o preço — não trava nada.
  // CPF (Douglas 29/09/2026): família do plano móvel e quantos dependentes ele leva,
  // lidos de planos_proposta_catalogo.metadata. Ver familia-cpf.ts.
  familiaMovel?: 'POS' | 'CONTROLE' | 'DEPENDENTE';
  planoClaro?: string;
  dependentesInclusos?: number;
  dependentesPagosMax?: number;
  dependente?: 'incluso' | 'pago' | 'dados';
  modalidade?: string;
}

// Tipos de linha na proposta. 'SERVICO' = linha data-driven de uma categoria
// customizada criada pelo admin em Planos & Preços (a categoria real fica em
// LinhaComposicao.categoriaCustom). Os 8 primeiros são os fixos do código.
export type TipoLinha = 'TT' | 'TT_PRE' | 'PORTABILIDADE' | 'NOVA' | 'FIBRA' | 'TV' | 'RENOVACAO' | 'LINHA_NOVA' | 'SERVICO';

// Sub-tipo de Bloco de Dados (apenas para tipo='NOVA') — define qual produto do pedido será gerado:
// 'NOVO' → HA NOVO (default, retrocompatível); 'INCREMENTO' → HA INCREMENTO; 'RENOVACAO' → RENOVAÇAO
export type TipoBloco = 'NOVO' | 'INCREMENTO' | 'RENOVACAO';

export type RenovacaoFlag = 'SIM' | 'NAO' | 'NAO_INFORMADO';

export interface RenovacaoSimulacao {
  flagForaAreaCabeada: RenovacaoFlag;
  flagBlFixaAtiva: RenovacaoFlag;
  flagConvergenciaAto: RenovacaoFlag;
  pedidoFixaVinculadoId: string | null;
  faturaUploadId: string | null;
  faturaUploadIds?: string[];
  mesCompetencia: string;
  modoAgrupamento: 'POR_LINHA';
  /**
   * MSISDNs (normalizados) que o consultor REMOVEU da proposta de renovação.
   * Sem isto o reconciliador com a fatura repunha toda linha apagada — a
   * consultora apagava a REN pra montar só um HA e ela "voltava" (Douglas
   * 29/08). A fatura manda nos DADOS da linha; a exclusão é decisão do
   * consultor e o reconciliador respeita.
   */
  linhasExcluidas?: string[];
}

export interface SugestaoRenovacaoLinha {
  origem: 'ANALISE_FATURA';
  planoId: string;
  planoNome: string;
  ticketNovo: number;
  deltaPercentual: number | null;
  faixa: number | null;
  acrescimoReceita: number;
  ticketAlvoFaixa1: number | null;
  motivo: string;
  aplicadaAutomaticamente: boolean;
}

/**
 * Tipo de chip da linha. Só existem estes dois na ativação: o REAL é o eSIM
 * digital, que ativa sozinho; o SIMCARD precisa de chip físico e abre pedido
 * de troca. Quem escolhe é o consultor, NO PEDIDO — a proposta não pergunta
 * (Douglas 22/09/2026). O valor legado 'e-sim' não é um terceiro tipo: era a
 * ausência de escolha, e era isso que parava o ciclo de Qualidade.
 */
export type TipoChipAtivacao = 'esim_real' | 'esim_simcard';

/**
 * A linha vai virar HA/PN no pedido — os produtos que passam pela ativação de
 * chip. Mesma régua de produtoExigeAtivacaoChip, decidida no tipo da linha:
 * PORTABILIDADE vira PN NOVO, LINHA_NOVA e NOVA viram HA. Bloco de renovação
 * não entra: RENOVAÇAO não é HA/PN. TT mantém o chip do cliente e por isso
 * também fica de fora.
 */
/**
 * TT E RENOVAÇÃO NÃO TÊM CHIP (Douglas 22/09/2026): nos dois a linha já é do
 * cliente e continua com o mesmo número e o mesmo chip. Não há eSIM novo, não
 * há SIMCARD, não há o que classificar — o item nasce sem chip nenhum.
 */
export function linhaTemChip(linha: Pick<LinhaComposicao,'tipo'>): boolean {
  return !['TT', 'TT_PRE', 'RENOVACAO'].includes(linha.tipo);
}

export function linhaExigeTipoChip(linha: Pick<LinhaComposicao,'tipo'|'tipoBloco'>): boolean {
  if (linha.tipo === 'PORTABILIDADE' || linha.tipo === 'LINHA_NOVA') return true;
  return linha.tipo === 'NOVA' && linha.tipoBloco !== 'RENOVACAO';
}

export interface LinhaComposicao {
  faturaUploadId?: string;
  contaNumero?: string;
  competenciaFatura?: string;
  enderecoInstalacao?: Record<string, unknown>;
  id: string;
  linha_id?: string; // ← UUID único e IMUTÁVEL - guardrail para merge no regenerar
  numero: string;
  tipo: TipoLinha;
  planoId: string | null;
  plano?: PlanoCNPJ;
  adicionais?: Record<string, number>; // { 'tv-ponto': 3, 'tv-ponto-4k': 1, 'passaport-america': 1 } - quantidade de cada adicional
  tipoFibra?: 'CNPJ' | 'TT'; // Apenas para FIBRA: CNPJ (padrão) ou TT (TT FIXA)
  tipoBloco?: TipoBloco; // Apenas para tipo='NOVA': determina se é Bloco Novo / Incremento / Renovação
  categoriaCustom?: string; // Apenas para tipo='SERVICO': categoria customizada do catálogo (ex.: 'servico_ti')
  /**
   * Quantidade de licenças na MESMA linha (só tipo='SERVICO'). Douglas 05/08:
   * "não está liberado para adicionar quantidade neste serviço digital... isso
   * facilita, para não repetir mil vezes o serviço se for o mesmo". Ausente = 1.
   */
  quantidade?: number;
  /**
   * Telefone ao qual este serviço está vinculado (só tipo='SERVICO'). Vira
   * `ddd` + `linha` no item do pedido, e é o que aparece na Planilha.
   * ⚠️ NÃO usar o campo `numero` para isso: o conversor deduplica linhas por
   * telefone, e um serviço com o mesmo número de uma linha móvel seria
   * descartado em silêncio.
   */
  linhaVinculada?: string;
  /**
   * IDENTIDADE do vínculo do serviço com a linha (Douglas 01/09/2026).
   * O telefone não serve de chave: linha de Bloco, Linha Nova e PN nascem SEM
   * número ('Bloco de Dados', 'Linha Nova', ''), e aí o serviço nunca casava —
   * o chip não acendia, cada clique criava outra licença e no pedido todas
   * colapsavam num item só, sem telefone. Este campo é o `linha_id` da linha
   * dona; `linhaVinculada` continua guardando o telefone (é ele que vira
   * ddd+linha no item e aparece na Planilha) e serve de reserva para as
   * composições salvas antes deste campo existir.
   */
  linhaVinculadaId?: string;
  /**
   * CPF (Douglas 29/09/2026): esta linha é DEPENDENTE do titular Pós cujo
   * `linha_id` está aqui. O mesmo plano não entra duas vezes no CPF; a segunda
   * pessoa em diante divide a internet do titular. Ver familia-cpf.ts.
   */
  titularLinhaId?: string;
  /**
   * Aparelho encaixado NESTA linha (Douglas 08/08). Fica na linha na UI, mas
   * no pedido vira ITEM SEPARADO com produto APARELHO (grão próprio na
   * planilha — é ele que a meta de 15/mês conta). O preço é resolvido pela
   * MATRIZ modelo×plano no momento da seleção: 24×/12× na fatura usam o
   * preço subsidiado do plano da linha; à vista usa o preço BASE sem subsídio.
   */
  aparelho?: {
    slug: string;
    nome: string;
    forma: '24x' | '12x' | 'avista';
    preco: number;
    parcela?: number;
    grupoUsado: string;
    faixaGb: number | null;
  };
  // Dados do plano atual (para renovação/comparativo)
  gbAtual?: number;
  valorAtual?: number;
  planoAtual?: string;
  planoNovo?: string;
  ticketNovo?: number;
  receitaRenovada?: number;
  receitaAdicionada?: number;
  sugestaoRenovacao?: SugestaoRenovacaoLinha;
}

export interface ComposicaoProposta {
  tipoPessoa?: 'PF' | 'PJ';
  linhas: LinhaComposicao[];
  tipoProposta?: 'RENOVACAO' | 'RENOVACAO_INCREMENTO';
  incluiFibra: boolean;
  planoFibra?: PlanoCNPJ;
  totalGB: number;
  totalValor: number;
  economiaValor: number;
  economiaPercent: number;
  renovacao?: RenovacaoSimulacao;
}

// ============= PLANOS MÓVEL CNPJ (Claro Pós Empresas) =============
export const PLANOS_MOVEL_CNPJ: PlanoCNPJ[] = [
  {
    id: 'pos-0',
    nome: 'Claro Pós 0GB',
    gb: 0,
    valor: 19.99,
    tipo: 'MOVEL',
    beneficios: [
      'Voz e SMS',
      'WhatsApp ilimitado',
      'Claro Monitor',
    ],
  },
  {
    id: 'pos-11',
    nome: 'Claro Pós 11GB',
    gb: 11,
    valor: 39.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'pos-25',
    nome: 'Claro Pós 25GB',
    gb: 25,
    valor: 49.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'pos-45',
    nome: 'Claro Pós 45GB',
    gb: 45,
    valor: 59.99,
    tipo: 'MOVEL',
    megabonus: true,
    destaque: true, // Custo-benefício
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'pos-70',
    nome: 'Claro Pós 70GB',
    gb: 70,
    valor: 69.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'pos-140',
    nome: 'Claro Pós 140GB',
    gb: 140,
    valor: 99.99,
    tipo: 'MOVEL',
    megabonus: true,
    destaque: true, // Premium
    passaporte: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
      'Claro Passaporte (roaming internacional)',
    ],
  },
  {
    id: 'pos-200',
    nome: 'Claro Pós 200GB',
    gb: 200,
    valor: 149.99,
    tipo: 'MOVEL',
    megabonus: true,
    destaque: true,
    passaporte: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Gestor Online',
      'McAfee Security',
      'Skeelo (livros)',
      'Claro Recados',
      'Claro Banca',
      'Claro Monitor',
      'Claro Passaporte (roaming internacional)',
      'Cloud 50GB',
    ],
  },
  // ============= PLANOS BLOCO DE DADOS (NOVA) =============
  { id: 'bloco-60',  nome: 'Bloco de Dados 60GB',  gb: 60,  valor: 174.99, tipo: 'MOVEL' as const, maxLinhasBan: 20,  beneficios: ['60GB compartilhados', 'Até 20 linhas/BAN'] },
  { id: 'bloco-70',  nome: 'Bloco de Dados 70GB',  gb: 70,  valor: 189.99, tipo: 'MOVEL' as const, maxLinhasBan: 23,  beneficios: ['70GB compartilhados', 'Até 23 linhas/BAN'] },
  { id: 'bloco-80',  nome: 'Bloco de Dados 80GB',  gb: 80,  valor: 199.99, tipo: 'MOVEL' as const, maxLinhasBan: 27,  beneficios: ['80GB compartilhados', 'Até 27 linhas/BAN'] },
  { id: 'bloco-90',  nome: 'Bloco de Dados 90GB',  gb: 90,  valor: 209.99, tipo: 'MOVEL' as const, maxLinhasBan: 30,  beneficios: ['90GB compartilhados', 'Até 30 linhas/BAN'] },
  { id: 'bloco-100', nome: 'Bloco de Dados 100GB', gb: 100, valor: 249.99, tipo: 'MOVEL' as const, maxLinhasBan: 33,  beneficios: ['100GB compartilhados', 'Até 33 linhas/BAN'] },
  { id: 'bloco-150', nome: 'Bloco de Dados 150GB', gb: 150, valor: 289.99, tipo: 'MOVEL' as const, maxLinhasBan: 50,  destaque: true, beneficios: ['150GB compartilhados', 'Até 50 linhas/BAN'] },
  { id: 'bloco-200', nome: 'Bloco de Dados 200GB', gb: 200, valor: 399.99, tipo: 'MOVEL' as const, maxLinhasBan: 67,  beneficios: ['200GB compartilhados', 'Até 67 linhas/BAN'] },
  { id: 'bloco-300', nome: 'Bloco de Dados 300GB', gb: 300, valor: 459.99, tipo: 'MOVEL' as const, maxLinhasBan: 100, beneficios: ['300GB compartilhados', 'Até 100 linhas/BAN'] },
  { id: 'bloco-400', nome: 'Bloco de Dados 400GB', gb: 400, valor: 499.99, tipo: 'MOVEL' as const, maxLinhasBan: 133, beneficios: ['400GB compartilhados', 'Até 133 linhas/BAN'] },
  { id: 'bloco-500', nome: 'Bloco de Dados 500GB', gb: 500, valor: 649.99, tipo: 'MOVEL' as const, maxLinhasBan: 167, beneficios: ['500GB compartilhados', 'Até 167 linhas/BAN'] },
  { id: 'bloco-600', nome: 'Bloco de Dados 600GB', gb: 600, valor: 669.99, tipo: 'MOVEL' as const, maxLinhasBan: 200, beneficios: ['600GB compartilhados', 'Até 200 linhas/BAN'] },
  { id: 'bloco-700', nome: 'Bloco de Dados 700GB', gb: 700, valor: 699.99, tipo: 'MOVEL' as const, maxLinhasBan: 233, beneficios: ['700GB compartilhados', 'Até 233 linhas/BAN'] },
  { id: 'bloco-800', nome: 'Bloco de Dados 800GB', gb: 800, valor: 709.99, tipo: 'MOVEL' as const, maxLinhasBan: 267, beneficios: ['800GB compartilhados', 'Até 267 linhas/BAN'] },
  { id: 'bloco-900', nome: 'Bloco de Dados 900GB', gb: 900, valor: 749.99, tipo: 'MOVEL' as const, maxLinhasBan: 300, beneficios: ['900GB compartilhados', 'Até 300 linhas/BAN'] },
];

// ============= PLANOS RENOVAÇÃO CNPJ (Portfólio Exclusivo Base Instalada) =============
// Para clientes CNPJ que já são Claro e querem renovar/upgrade
export const PLANOS_RENOVACAO_CNPJ: PlanoCNPJ[] = [
  {
    id: 'ren-0',
    nome: 'Renovação 0GB',
    gb: 0,
    valor: 19.99,
    tipo: 'MOVEL',
    beneficios: ['Voz e SMS', 'WhatsApp ilimitado', 'Claro Monitor'],
  },
  {
    id: 'ren-11',
    nome: 'Renovação 11GB',
    gb: 11,
    valor: 39.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'ren-10',
    nome: 'Renovação 10GB',
    gb: 10,
    valor: 44.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'ren-25',
    nome: 'Renovação 25GB',
    gb: 25,
    valor: 54.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'ren-40',
    nome: 'Renovação 40GB',
    gb: 40,
    valor: 64.99,
    tipo: 'MOVEL',
    megabonus: true,
    destaque: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'ren-65',
    nome: 'Renovação 65GB',
    gb: 65,
    valor: 74.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
];

// ============= PLANOS PORTABILIDADE CNPJ (Vivo, Tim, etc. → Claro) =============
export const PLANOS_PORTABILIDADE_CNPJ: PlanoCNPJ[] = [
  {
    id: 'port-0',
    nome: 'Portabilidade 0GB',
    gb: 0,
    valor: 19.99,
    tipo: 'MOVEL',
    beneficios: ['Voz e SMS', 'WhatsApp ilimitado', 'Claro Monitor'],
  },
  {
    id: 'port-6',
    nome: 'Portabilidade 6GB',
    gb: 6,
    valor: 29.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'port-21',
    nome: 'Portabilidade 21GB',
    gb: 21,
    valor: 39.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'port-40',
    nome: 'Portabilidade 40GB',
    gb: 40,
    valor: 49.99,
    tipo: 'MOVEL',
    megabonus: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'port-70',
    nome: 'Portabilidade 70GB',
    gb: 70,
    valor: 59.99,
    tipo: 'MOVEL',
    megabonus: true,
    destaque: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
    ],
  },
  {
    id: 'port-140',
    nome: 'Portabilidade 140GB',
    gb: 140,
    valor: 99.99,
    tipo: 'MOVEL',
    megabonus: true,
    destaque: true,
    passaporte: true,
    beneficios: [
      'WhatsApp ilimitado',
      'Waze ilimitado',
      'Instagram incluso',
      'Facebook incluso',
      'Messenger incluso',
      'X (Twitter) incluso',
      'Claro Monitor',
      'McAfee Security',
      'Claro Passaporte (roaming internacional)',
    ],
  },
];

// ============= PLANOS FIBRA =============
export const PLANOS_FIBRA: PlanoCNPJ[] = [
  {
    id: 'fibra-400',
    nome: 'Claro Fibra 400MB',
    gb: 0,
    valor: 79.90,
    tipo: 'FIBRA',
    velocidade: '400MB',
    beneficios: ['Internet Fibra 400 Mega', 'Wi-Fi grátis', 'Instalação grátis'],
  },
  {
    id: 'fibra-600',
    nome: 'Claro Fibra 600MB',
    gb: 0,
    valor: 99.90,
    tipo: 'FIBRA',
    velocidade: '600MB',
    beneficios: ['Internet Fibra 600 Mega', 'Wi-Fi grátis', 'Instalação grátis'],
  },
  {
    id: 'fibra-600-convergente',
    nome: '600MB Convergente',
    gb: 0,
    valor: 89.90,
    tipo: 'FIBRA',
    velocidade: '600MB',
    beneficios: ['Internet Fibra 600 Mega', 'Wi-Fi grátis', 'Instalação grátis', 'Valor Convergente'],
  },
  {
    id: 'fibra-600-84',
    nome: 'Claro Fibra 600MB',
    gb: 0,
    valor: 84.90,
    tipo: 'FIBRA',
    velocidade: '600MB',
    beneficios: ['Internet Fibra 600 Mega', 'Wi-Fi grátis', 'Instalação grátis'],
  },
  {
    id: 'fibra-800',
    nome: 'Claro Fibra 800MB',
    gb: 0,
    valor: 109.90,
    tipo: 'FIBRA',
    velocidade: '800MB',
    beneficios: ['Internet Fibra 800 Mega', 'Wi-Fi grátis', 'Instalação grátis'],
  },
];

// ============= PLANOS TV BASE (Claro TV Empresas) =============
// Planos principais de TV (escolha obrigatória)
export const PLANOS_TV: PlanoCNPJ[] = [
  {
    id: 'tv-compacto',
    nome: 'Compacto HD Claro TV',
    gb: 0,
    valor: 84.90,
    tipo: 'COMBO',
    beneficios: ['88 canais em HD', 'Áreas HFC e GPON', 'Instalação grátis'],
  },
  {
    id: 'tv-4k',
    nome: '4K Claro TV',
    gb: 0,
    valor: 144.90,
    tipo: 'COMBO',
    beneficios: ['Canais em 4K', 'Áreas HFC', 'Instalação grátis'],
  },
  {
    id: 'tv-hd',
    nome: 'HD Claro TV',
    gb: 0,
    valor: 144.90,
    tipo: 'COMBO',
    beneficios: ['Canais em HD Premium', 'Áreas GPON', 'Instalação grátis'],
  },
  {
    id: 'tv-tt',
    nome: 'TT Claro TV',
    gb: 0,
    valor: 0,
    tipo: 'COMBO',
    beneficios: ['TV TT', 'Sem custo adicional'],
  },
];

// ============= ADICIONAIS DE TV (opcionais, após escolher plano base) =============
export interface AdicionalTV {
  id: string;
  nome: string;
  valor: number;
  restricao?: string; // ID do plano que restringe este adicional (ex: 'tv-4k' para Ponto 4K)
}

export const ADICIONAIS_TV: AdicionalTV[] = [
  { id: 'tv-ponto', nome: 'Ponto Adicional', valor: 29.90 },
  { id: 'tv-ponto-4k', nome: 'Ponto Adicional 4K', valor: 39.90, restricao: 'tv-4k' },
];

// ============= ADICIONAIS DE FIBRA (opcionais, após escolher fibra) =============
export interface AdicionalFibra {
  id: string;
  nome: string;
  valor: number;
  maxQuantidade?: number; // Máximo que pode adicionar
  descricao?: string;
}

export const ADICIONAIS_FIBRA: AdicionalFibra[] = [
  {
    id: 'fibra-claro-fixo',
    nome: 'Claro Fixo',
    valor: 10.00,
    descricao: 'Linha fixa por linha'
  },
  {
    id: 'fibra-mesh-2',
    nome: '2 Extensores Wi-Fi Mesh',
    valor: 30.00,
    maxQuantidade: 1,
    descricao: 'Primeira compra - 2 extensores'
  },
  {
    id: 'fibra-mesh-adicional',
    nome: 'Extensor Wi-Fi Mesh Adicional',
    valor: 15.00,
    maxQuantidade: 2,
    descricao: 'Extensor adicional'
  },
];

// ============= ADICIONAIS MÓVEL (Passaport para TT, PN, HA, Renovação) =============
export interface AdicionalMovel {
  id: string;
  nome: string;
  valor: number;
  produtoId: string; // ID do produto no banco (para criar item separado)
  gb?: number | null;
  descricao?: string;
}

export const ADICIONAIS_MOVEL: AdicionalMovel[] = [
  {
    id: 'passaport-america',
    nome: NOMES_ADICIONAIS_MOVEL['passaport-america'],
    valor: 14.90,
    produtoId: '8990886b-21a2-4060-8eae-ecc69b6d5182',
    descricao: 'Roaming nas Américas'
  },
  {
    id: 'passaport-europa',
    nome: NOMES_ADICIONAIS_MOVEL['passaport-europa'],
    valor: 24.90,
    produtoId: 'c056d863-c8fd-46c7-8647-c882c3801258',
    descricao: 'Roaming na Europa'
  },
  {
    id: 'passaport-mundo',
    nome: NOMES_ADICIONAIS_MOVEL['passaport-mundo'],
    valor: 34.99,
    produtoId: '9ab43a32-6aa1-4d9e-8ad3-f6b35d7cde4c',
    descricao: 'Roaming mundial'
  },
];

let adicionaisMovelRuntime = new Map<string, AdicionalMovel>();

export function registrarAdicionaisMovelRuntime(adicionais: AdicionalMovel[]): void {
  adicionaisMovelRuntime = new Map(adicionais.map((adicional) => [adicional.id, adicional]));
}

/** Adicionais de TV/fibra que só existem no banco (CPF): entram nas mesmas listas que os
 *  cálculos de valor e a conversão em pedido já consultam. Os estáticos não são alterados. */
export function registrarAdicionaisCatalogoRuntime(tv: AdicionalTV[], fibra: AdicionalFibra[]): void {
  const juntar = <T extends { id: string }>(lista: T[], novos: T[]) => {
    for (const novo of novos) {
      const i = lista.findIndex((a) => a.id === novo.id);
      if (i >= 0) { if (novo.id.startsWith('cpf-')) lista[i] = novo; } else lista.push(novo);
    }
  };
  juntar(ADICIONAIS_TV, tv.filter((a) => a.id.startsWith('cpf-')));
  juntar(ADICIONAIS_FIBRA, fibra.filter((a) => a.id.startsWith('cpf-')));
}

export function getAdicionalMovelById(id: string): AdicionalMovel | undefined {
  return adicionaisMovelRuntime.get(id) ?? ADICIONAIS_MOVEL.find((adicional) => adicional.id === id);
}

// ============= PLANOS COMBO (aguardando envio pelo usuário) =============
export const PLANOS_COMBO: PlanoCNPJ[] = [
  // Placeholder - serão adicionados quando o usuário enviar
];

// ============= CENÁRIOS PRÉ-CONFIGURADOS =============
export interface CenarioProposta {
  id: string;
  nome: string;
  descricao: string;
  icone: string;
  cor: 'red' | 'emerald' | 'amber' | 'gray'; // Cores Claro
  regra: 'ECONOMIA_MAXIMA' | 'UPGRADE_LINHA_NOVA' | 'COMBO_SIMILAR' | 'EQUILIBRIO';
  incliFibra: boolean;
  incluiLinhaNova: boolean;
  // Função que recebe qtd de linhas e retorna planos sugeridos
  getPlanosPorLinha: (qtdLinhas: number) => {
    tt: string; // ID do plano para linhas TT (CPF Claro → CNPJ Claro)
    nova: string; // ID do plano para linha nova
    fibra?: string; // ID do plano fibra
  };
}

export const CENARIOS_PROPOSTA: CenarioProposta[] = [
  {
    id: 'combo-similar',
    nome: 'Combo Similar ao Atual',
    descricao: 'Fibra 400MB + linhas móveis que chegam próximo ao valor atual. Melhor custo-benefício!',
    icone: '🎯',
    cor: 'red', // Cor Claro principal
    regra: 'COMBO_SIMILAR',
    incliFibra: true,
    incluiLinhaNova: false,
    getPlanosPorLinha: () => ({
      tt: 'pos-45',
      nova: 'pos-45',
      fibra: 'fibra-400',
    }),
  },
  {
    id: 'economia',
    nome: 'Economia Máxima',
    descricao: 'Todas as linhas TT com plano básico de 11GB. Maior economia mantendo os serviços.',
    icone: '💰',
    cor: 'emerald',
    regra: 'ECONOMIA_MAXIMA',
    incliFibra: false,
    incluiLinhaNova: false,
    getPlanosPorLinha: () => ({
      tt: 'pos-11',
      nova: 'pos-11',
    }),
  },
  {
    id: 'upgrade',
    nome: 'Upgrade + Bloco de Dados',
    descricao: 'Bloco de Dados com 140GB, existentes TT com 25GB. Mais dados para o cliente.',
    icone: '🚀',
    cor: 'amber',
    regra: 'UPGRADE_LINHA_NOVA',
    incliFibra: false,
    incluiLinhaNova: true,
    getPlanosPorLinha: () => ({
      tt: 'pos-25',
      nova: 'pos-140',
    }),
  },
  {
    id: 'equilibrado',
    nome: 'Equilíbrio',
    descricao: 'Todas as linhas TT com 45GB. Bom custo-benefício com dados generosos.',
    icone: '⚖️',
    cor: 'gray',
    regra: 'EQUILIBRIO',
    incliFibra: false,
    incluiLinhaNova: false,
    getPlanosPorLinha: () => ({
      tt: 'pos-45',
      nova: 'pos-70',
    }),
  },
];

// ============= HELPERS =============

/**
 * Registro runtime do catálogo do banco (planos_proposta_catalogo).
 * É preenchido pelo hook usePlanosPropostaCatalogo quando o catálogo carrega.
 * getPlanoById consulta o RUNTIME (banco) primeiro — o painel de Planos é a fonte
 * da verdade do preço/GB. As linhas do banco já vêm enriquecidas com benefícios/
 * megabônus do estático (rowToPlano), então o estático fica só como fallback para
 * quando o catálogo do banco ainda não carregou ou não tem aquele código. Assim o
 * preço editado no painel resolve em TODA a cadeia (montador, hidratação, conversão
 * em pedido) — sem divergência entre o que a proposta mostra e o que vira pedido.
 */
const _catalogoRuntime = new Map<string, PlanoCNPJ>();
// Mesmo catalogo, chaveado por `categoria::codigo` - para quem SABE a categoria.
const _catalogoPorCategoria = new Map<string, PlanoCNPJ>();

/**
 * Categoria do catalogo que corresponde ao tipo da linha da proposta.
 * Regua UNICA: o montador (catDoTipo) e o conversor/revisao resolvem por aqui.
 * Com duas copias, o produto do pedido passava a depender de qual tela criou a linha.
 */
export function categoriaDoTipo(tipo: TipoLinha, tipoBloco?: string | null, categoriaCustom?: string | null): string {
  if (categoriaCustom) return categoriaCustom;
  switch (tipo) {
    case 'TT': return 'tt_pos';
    case 'TT_PRE': return 'tt_pre';
    case 'PORTABILIDADE': return 'pn_novo';
    case 'LINHA_NOVA': return 'nova';
    case 'RENOVACAO': return 'renov';
    case 'NOVA': return tipoBloco === 'INCREMENTO' ? 'bloco_incr' : tipoBloco === 'RENOVACAO' ? 'bloco_renov' : 'bloco_novo';
    case 'FIBRA': return 'fibra';
    case 'TV': return 'tv';
    case 'SERVICO': return categoriaCustom ?? '';
    default: return 'tt_pos';
  }
}

/**
 * O CODIGO DO PLANO NAO E UNICO (Douglas 20/08/2026).
 *
 * `pos-11` existe em `movel_migracao`, `nova` (HA NOVO) e `tt_pos` (TT POS-PJ);
 * `port-25` existe em `pn_novo` e `pn_incremento`. Nome, valor e GB sao IGUAIS
 * entre elas - so o `produto_pedido_id` muda. Como este Map e chaveado so pelo
 * codigo, a ultima categoria carregada vencia (ordem alfabetica: `tt_pos`), e uma
 * "Linha Nova" virava TT POS-PJ no pedido: sai de ALTAS/VENDA NOVA e cai em
 * PP/MIGRACAO, mexendo em meta e comissao sem ninguem pedir. Aconteceu 4 vezes
 * entre 24/06 e 14/08.
 *
 * Regra: codigo ambiguo + categoria desconhecida = NAO se chuta o produto. O plano
 * entra com `produtoPedidoId: null` e quem decide passa a ser o mapeamento por tipo
 * (`proposta_produto_mapeamento`). Preco/GB/nome seguem valendo normalmente. Mesma
 * postura de `buscarProdutoPorCodigoUnico` no conversor.
 */
export function registrarCatalogoRuntime(planos: PlanoCNPJ[]): void {
  _catalogoRuntime.clear();
  _catalogoPorCategoria.clear();

  const produtosPorCodigo = new Map<string, Set<string>>();
  for (const p of planos) {
    if (!p.produtoPedidoId) continue;
    const s = produtosPorCodigo.get(p.id) ?? new Set<string>();
    s.add(p.produtoPedidoId);
    produtosPorCodigo.set(p.id, s);
  }

  for (const p of planos) {
    if (p.categoria) _catalogoPorCategoria.set(`${p.categoria}::${p.id}`, p);
    const ambiguo = (produtosPorCodigo.get(p.id)?.size ?? 0) > 1;
    _catalogoRuntime.set(p.id, ambiguo ? { ...p, produtoPedidoId: null } : p);
  }
}

export function getPlanoById(id: string, categoria?: string | null): PlanoCNPJ | undefined {
  if (categoria) {
    const exato = _catalogoPorCategoria.get(`${categoria}::${id}`);
    if (exato) return exato;
  }
  const runtime = _catalogoRuntime.get(id);
  if (runtime) return runtime;
  return [...PLANOS_MOVEL_CNPJ, ...PLANOS_RENOVACAO_CNPJ, ...PLANOS_PORTABILIDADE_CNPJ, ...PLANOS_FIBRA, ...PLANOS_TV, ...PLANOS_COMBO].find(p => p.id === id);
}

export function calcularTotalGB(linhas: LinhaComposicao[], _incluiFibra: boolean): number {
  let total = 0;
  linhas.forEach(linha => {
    if (linha.categoriaCustom === 'aparelhos' || linha.tipo === 'SERVICO') return;
    if (linha.plano && linha.tipo !== 'FIBRA') {
      const gb = Number(linha.plano.gb);
      if (Number.isFinite(gb)) total += gb;
    }
  });
  // Megabônus NÃO é somado aqui - é exibido separadamente na UI
  // Isso evita duplicação quando a UI também mostra "+30GB"
  return total;
}

/**
 * Calcula o valor total da composição de proposta.
 * IMPORTANTE: Passa TODAS as linhas (incluindo FIBRA) - a função soma tudo internamente.
 * Não filtre linhas antes de chamar esta função!
 */
export function calcularTotalValor(linhas: LinhaComposicao[]): number {
  let total = 0;

  linhas.forEach(linha => {
    // Compatibilidade com propostas antigas que gravaram aparelho como
    // SERVICO. Aparelho é venda avulsa/parcelada e não mensalidade.
    if (linha.categoriaCustom === 'aparelhos') return;
    // Somar valor base do plano (móvel, fibra, TV, etc.)
    // Serviço digital multiplica pelas licenças da própria linha: 50 licenças de
    // M365 numa linha só custam 50×, não 1×. As demais linhas são sempre 1 unidade.
    if (linha.plano) {
      total += linha.plano.valor * (linha.tipo === 'SERVICO' ? Math.max(1, linha.quantidade ?? 1) : 1);
    }

    // Somar adicionais (TV, Fibra, Móvel - todos são tratados igualmente)
    if (linha.adicionais) {
      Object.entries(linha.adicionais).forEach(([adicId, quantidade]) => {
        // Verificar em ADICIONAIS_TV
        const adicionalTV = ADICIONAIS_TV.find(a => a.id === adicId);
        if (adicionalTV && quantidade > 0) {
          total += adicionalTV.valor * quantidade;
        }
        // Verificar em ADICIONAIS_FIBRA (Claro Fixo, Mesh, etc.)
        const adicionalFibra = ADICIONAIS_FIBRA.find(a => a.id === adicId);
        if (adicionalFibra && quantidade > 0) {
          total += adicionalFibra.valor * quantidade;
        }
        // Verificar em ADICIONAIS_MOVEL (Passaportes: América, Europa, Mundo)
        const adicionalMovel = getAdicionalMovelById(adicId);
        if (adicionalMovel && quantidade > 0) {
          total += adicionalMovel.valor * quantidade;
        }
      });
    }
  });

  // Arredondar para 2 casas decimais para evitar loop de ponto flutuante
  return Math.round(total * 100) / 100;
}

export function formatarMoedaPlano(valor: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor);
}

// Gerar composição baseada em cenário - suporta TT e RENOVACAO
export function gerarComposicaoCenario(
  cenarioId: string,
  linhasAtuais: string[], // Números de telefone atuais
  valorAtual: number = 0,
  tipoBase: TipoLinha = 'TT' // Tipo das linhas: 'TT' para CPF→CNPJ, 'RENOVACAO' para CNPJ→CNPJ
): ComposicaoProposta | null {
  const cenario = CENARIOS_PROPOSTA.find(c => c.id === cenarioId);
  if (!cenario) return null;

  const config = cenario.getPlanosPorLinha(linhasAtuais.length);
  const planoLinhas = getPlanoById(config.tt);
  const planoNova = getPlanoById(config.nova);
  const planoFibra = config.fibra ? getPlanoById(config.fibra) : undefined;

  if (!planoLinhas) return null;

  // Linhas com tipo dinâmico (TT ou RENOVACAO)
  const tipoId = tipoBase.toLowerCase();
  const linhas: LinhaComposicao[] = linhasAtuais.map((numero, index) => ({
    id: `${tipoId}-${index}`,
    numero,
    tipo: tipoBase,
    planoId: config.tt,
    plano: planoLinhas,
  }));

  // Adicionar bloco de dados se cenário pede
  if (cenario.incluiLinhaNova && planoNova) {
    linhas.push({
      id: 'nova-1',
      numero: 'Bloco de Dados',
      tipo: 'NOVA' as TipoLinha,
      planoId: config.nova,
      plano: planoNova,
    });
  }

  // Adicionar fibra se cenário pede
  if (cenario.incliFibra && planoFibra) {
    linhas.push({
      id: 'fibra-1',
      numero: 'Fibra',
      tipo: 'FIBRA' as TipoLinha,
      planoId: config.fibra!,
      plano: planoFibra,
    });
  }

  const incluiFibra = cenario.incliFibra;
  const totalGB = calcularTotalGB(linhas, incluiFibra);
  const totalValor = calcularTotalValor(linhas);
  const economiaValor = valorAtual - totalValor;
  const economiaPercent = valorAtual > 0 ? (economiaValor / valorAtual) * 100 : 0;

  return {
    linhas,
    incluiFibra,
    planoFibra,
    totalGB,
    totalValor,
    economiaValor,
    economiaPercent,
  };
}

// Descrição amigável do tipo de linha
export function getTipoLinhaLabel(tipo: TipoLinha): { label: string; descricao: string } {
  switch (tipo) {
    case 'TT':
      return { label: 'TT POS', descricao: 'TT Pós-Pago (CPF Claro Pós → CNPJ Claro)' };
    case 'TT_PRE':
      return { label: 'TT PRE', descricao: 'TT Pré-Pago (CPF Claro Pré → CNPJ Claro)' };
    case 'PORTABILIDADE':
      return { label: 'PN NOVO', descricao: 'Portabilidade Nova (Vivo, Tim, etc. → Claro)' };
    case 'NOVA':
      return { label: 'BLOCO', descricao: 'Bloco de Dados (pacote adicional de dados)' };
    case 'FIBRA':
      return { label: 'FIBRA', descricao: 'Internet Fibra Residencial' };
    case 'TV':
      return { label: 'TV', descricao: 'Claro TV Empresas' };
    case 'RENOVACAO':
      return { label: 'REN', descricao: 'Renovação de linha CNPJ existente' };
    case 'LINHA_NOVA':
      return { label: 'NOVA', descricao: 'Habilitação de linha nova Claro' };
    case 'SERVICO':
    default:
      return { label: 'SERVIÇO', descricao: 'Serviço / produto extra (categoria customizada)' };
  }
}

// Labels curtos para mensagem
export const TIPO_LABEL_MSG: Record<TipoLinha, string> = {
  TT: 'TT POS',
  TT_PRE: 'TT PRE',
  PORTABILIDADE: 'PN NOVO',
  NOVA: 'BLOCO',
  FIBRA: 'FIBRA',
  TV: 'TV',
  RENOVACAO: 'REN',
  LINHA_NOVA: 'NOVA',
  SERVICO: 'SERVIÇO',
};

// Helper para formatar telefone
export function formatarTelefoneLocal(telefone: string): string {
  const digitos = telefone.replace(/\D/g, '');
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  } else if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }
  return telefone;
}

// ============= GERADOR DE MENSAGEM COMPLETA DE PROPOSTA =============
export interface GerarMensagemPropostaParams {
  nomeCliente?: string;
  razaoSocial?: string;
  origemLabel: 'PF' | 'CNPJ' | 'VIVO';
  planoAtual: {
    linhas: string[];
    qtdLinhas: number; // Quantidade editável de linhas do plano atual
    gb?: number;
    fibra?: string;
    tv?: string;
    valor: number;
  };
  composicaoNova: ComposicaoProposta;
  consultorNome?: string;
  ocultarPlanoAtual?: boolean; // Novo: omitir seção "Seu plano hoje" da mensagem
  /**
   * Frases de benefício dos produtos da composição, escritas em Configurações >
   * Produtos (`produtos.beneficio_proposta`). Quem resolve é o componente, que
   * tem a lista de produtos — este arquivo é de constantes e não consulta banco.
   */
  beneficiosDosProdutos?: string[];
}

/**
 * Gera a mensagem completa de proposta WhatsApp.
 * Modelo comercial e persuasivo para envio direto ao cliente.
 */
export function gerarMensagemPropostaCompleta(params: GerarMensagemPropostaParams): string {
  return gerarPropostaCanonica({ ...params,
    ...montarComplementosProposta(params.composicaoNova.linhas, id => getAdicionalMovelById(id)?.nome),
  });
}

// ============= GERADOR DE MENSAGEM DE RETORNO DINÂMICA =============
export interface GerarMensagemRetornoParams {
  nomeCliente?: string;
  razaoSocial?: string;
  consultorNome?: string;
  valorAtual: number;
  composicaoNova: ComposicaoProposta;
  tipoLead: string;
}

/**
 * Gera mensagem de RETORNO/follow-up baseada na proposta real.
 * Dinâmica conforme produtos da composição.
 */
export function gerarMensagemRetornoCompleta(params: GerarMensagemRetornoParams): string {
  const { nomeCliente, razaoSocial, consultorNome, valorAtual, composicaoNova } = params;
  // ⛔ CPF (Douglas 25/09/2026, texto aprovado): retoma o comparativo — serve para reabrir a janela.
  if (ehPropostaCpf(composicaoNova)) {
    const nome = primeiroNome(nomeCliente || razaoSocial);
    return [
      `Olá${nome ? `, ${nome}` : ''}. Aqui é ${consultorNome || 'seu consultor'}, da Claro.`,
      `Estou retornando sobre o comparativo da sua conta que te enviei: ${descricaoQuantidadesProposta(composicaoNova.linhas) || 'sua proposta'} por ${formatarMoedaPlano(composicaoNova.totalValor)}/mês.`,
      'Ainda está válido. Posso te ajudar a seguir?',
    ].join('\n');
  }

  const economia = valorAtual - composicaoNova.totalValor;


  const partes: string[] = [];

  partes.push(`Olá ${nomeCliente || 'tudo bem'}?`);
  partes.push('');
  const marcaR = marcaDaProposta(composicaoNova);
  partes.push(`Aqui é *${consultorNome || 'Consultor'}*, da *${marcaR.empresa}*.`);
  partes.push('');

  partes.push(`Sobre a proposta para *${razaoSocial || nomeCliente || marcaR.cliente}*: ${operacoesDaProposta(composicaoNova).join(' · ')}.`);
  partes.push('');

  // Resumo dinâmico
  partes.push('*Lembra da proposta?*');
  if (valorAtual > 0) {
    partes.push(`• De ${formatarMoedaPlano(valorAtual)}/mês para ${formatarMoedaPlano(composicaoNova.totalValor)}/mês`);
  } else {
    partes.push(`• ${formatarMoedaPlano(composicaoNova.totalValor)}/mês`);
  }
  if (economia > 0) {
    partes.push(`• Economia de ${formatarMoedaPlano(economia)}/mês`);
  }
  const servicosRetorno = listarServicosDigitais(composicaoNova.linhas);
  if (servicosRetorno.length > 0) partes.push(...servicosRetorno);
  partes.push('');

  partes.push('Conseguiu analisar? Posso esclarecer alguma dúvida?');

  return partes.join('\n');
}

// ============= GERADOR DE MENSAGEM DE FECHAMENTO DINÂMICA =============
export interface GerarMensagemFechamentoParams {
  nomeCliente?: string;
  razaoSocial?: string;
  consultorNome?: string;
  valorAtual: number;
  composicaoNova: ComposicaoProposta;
  tipoLead: string;
  /** Dados do cliente CPF para a conferência do fechamento. */
  clienteCpf?: { cpf?: string | null; nome?: string | null; endereco?: string | null; telefone?: string | null; email?: string | null };
}

/**
 * Gera mensagem de FECHAMENTO baseada na proposta real.
 * Mostra APENAS os produtos que existem na composição.
 */
export function gerarMensagemFechamentoCompleta(params: GerarMensagemFechamentoParams): string {
  const { nomeCliente, razaoSocial, composicaoNova } = params;
  // ⛔ CPF (Douglas 25/09/2026): o mesmo modelo do aviso "PEDIDO FECHADO" CPF,
  // com as frases quebradas. Lead e pedido falam com o mesmo texto.
  if (ehPropostaCpf(composicaoNova)) {
    const c = params.clienteCpf || {};
    const cpf = String(c.cpf || '').replace(/\D/g, '');
    const cpfFmt = cpf.length === 11 ? `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}` : (c.cpf || '—');
    const nome = primeiroNome(nomeCliente || c.nome || razaoSocial);
    return [
      `Olá${nome ? `, ${nome}` : ''}! Seu pedido foi registrado.`,
      'Confira os dados:',
      '',
      `*CPF:* ${cpfFmt}`,
      `*Nome:* ${c.nome || razaoSocial || nomeCliente || '—'}`,
      `*Endereço de instalação:* ${c.endereco || '—'}`,
      `*Contato:* ${c.telefone || '—'}`,
      `*E-mail:* ${c.email || '—'}`,
      '',
      '*O que você contratou:*',
      ...itensDaPropostaCpf(composicaoNova),
      `Total: ${formatarMoedaPlano(composicaoNova.totalValor)}/mês`,
      '*Vencimento da fatura:* dia 10',
      '',
      'O plano tem vigência de 12 meses.',
      'Se cancelar antes desse prazo, é cobrada multa proporcional de R$ 25,00 por mês que faltar.',
      '',
      'Está tudo certo?',
      'Responda *SIM* para confirmar ou me avise se algo estiver diferente.',
    ].join('\n');
  }

  const partes: string[] = [];

  partes.push(`Olá ${nomeCliente || 'Cliente'}!`);
  partes.push('');
  partes.push('Que ótimo fechar com você!');
  partes.push('');

  // (Douglas 15/07) "Migração" SÓ quando há linha móvel de verdade. Proposta só de
  // fibra/TV é CONTRATO NOVO — cliente lia "migração das suas linhas" e entrava em
  // pânico achando que mexeríamos nas linhas dele ("eu não autorizei migração").
  // (04/08) SERVICO (Serviço Digital: M365, Claro Monitor, McAfee, Workspace) NÃO é
  // linha móvel — é licença. Contado junto, o cliente lia "3 linhas móveis CNPJ"
  // quando uma delas era uma licença de software.
  const marcaF = marcaDaProposta(composicaoNova);
  const contexto = `Vou preparar o pedido para *${razaoSocial || nomeCliente || marcaF.cliente}*: ${operacoesDaProposta(composicaoNova).join(' · ')}.`;
  partes.push(contexto);
  partes.push('');

  // Usar totais já calculados na composição
  const totalValor = composicaoNova?.totalValor || 0;
  const linhasMovelF = composicaoNova.linhas.filter(l => ehLinhaDeVoz(l.tipo));
  const blocosF = composicaoNova.linhas.filter(l => l.tipo === 'NOVA');
  const linhasFibraF = composicaoNova.linhas.filter(l => l.tipo === 'FIBRA');
  const linhasTVF = composicaoNova.linhas.filter(l => l.tipo === 'TV');
  const linhasServicoF = composicaoNova.linhas.filter(l => l.tipo === 'SERVICO');

  partes.push('*Plano Aprovado:*');
  if (linhasMovelF.length > 0) {
    const gbMovelF = calcularTotalGB(linhasMovelF, false);
    const rotuloF = (linhasMovelF.length > 1 ? 'linhas móveis' : 'linha móvel') + (marcaF.cpf ? '' : ' CNPJ');
    const gbTxtF = gbMovelF > 0 ? ` • ${gbMovelF}GB` : '';
    partes.push(`${linhasMovelF.length} ${rotuloF}${gbTxtF} • ${formatarMoedaPlano(calcularTotalValor(linhasMovelF))}/mês`);
  }
  for (const bloco of blocosF) {
    partes.push(`• Bloco de dados ${bloco.plano?.nome || ''} • ${formatarMoedaPlano(calcularTotalValor([bloco]))}/mês`);
  }
  for (const lf of linhasFibraF) {
    const nfF = descricaoFibraDaProposta(lf.plano);
    partes.push(`${('Internet ' + nfF).trim()} • ${formatarMoedaPlano(calcularTotalValor([lf]))}/mês${marcaF.cpf ? promocaoDoPlano(lf.plano) : ''}`);
  }
  // Mega Bônus no fechamento (Douglas 09/10/2026): o cliente confirma o mesmo
  // benefício que leu na proposta. Mesma régua da proposta (megabonusDaProposta).
  const megabonusF = megabonusDaProposta(composicaoNova);
  if (megabonusF > 0) partes.push(`+${megabonusF}GB Megabônus com Fibra • sem custo`);
  for (const ltF of linhasTVF) {
    const ntF = ltF.plano?.nome || marcaF.tv;
    partes.push(`${ntF} • ${formatarMoedaPlano(calcularTotalValor([ltF]))}/mês`);
  }
  // Serviço digital: agrupa licença igual (5× Business Basic = uma linha só).
  // A quantidade vem do campo da linha (06/08) — antes contava 1 por linha, e uma
  // linha com 50 licenças aparecia no fechamento como se fosse uma só.
  const servicoPorPlanoF = new Map<string, { nome: string; qtd: number; valor: number; linhas: string[] }>();
  for (const lsF of linhasServicoF) {
    const chave = lsF.planoId || lsF.plano?.nome || 'servico';
    const licencas = Math.max(1, Math.floor(Number(lsF.quantidade) || 1));
    const vinculo = String(lsF.linhaVinculada ?? '').replace(/\D/g, '');
    const atual = servicoPorPlanoF.get(chave);
    if (atual) {
      atual.qtd += licencas;
      if (vinculo.length >= 10 && !atual.linhas.includes(vinculo)) atual.linhas.push(vinculo);
    } else {
      servicoPorPlanoF.set(chave, {
        nome: lsF.plano?.nome || 'Serviço',
        qtd: licencas,
        valor: lsF.plano?.valor || 0,
        linhas: vinculo.length >= 10 ? [vinculo] : [],
      });
    }
  }
  for (const sF of servicoPorPlanoF.values()) {
    const qtdTxtF = sF.qtd > 1 ? `${sF.qtd}× ` : '';
    const ondeF = sF.linhas.length === 1
      ? ` (linha ${formatarTelefoneExibicao(sF.linhas[0])})`
      : sF.linhas.length > 1 ? ` (${sF.linhas.length} linhas)` : '';
    partes.push(`${qtdTxtF}${sF.nome}${ondeF} • ${formatarMoedaPlano(sF.valor * sF.qtd)}/mês`);
  }
  // Aparelho no fechamento: FORA do total mensal (e parcelado na fatura), mas
  // escrito, senao o cliente assina sem ver o celular que comprou.
  const aparelhosF = listarAparelhos(composicaoNova.linhas);
  for (const apF of aparelhosF) partes.push(`${apF.texto}`);
  partes.push('');
  partes.push(`*Total: ${formatarMoedaPlano(totalValor)}/mês*`);
  if (aparelhosF.length > 0) {
    partes.push(`_(o aparelho e cobrado na propria fatura, conforme acima)_`);
  }
  partes.push('');

  const temMovelF = linhasMovelF.length > 0;
  const temInstalacaoF = linhasFibraF.some(l => l.tipoFibra !== 'TT') || linhasTVF.length > 0;
  const passosF: string[] = [];
  passosF.push('Vou gerar o pedido no sistema');
  passosF.push('Você receberá o contrato para assinatura digital');
  if (temMovelF) passosF.push(`Confirmação da sua identidade via biometria facial pelo link oficial da ${marcaF.operadora}`);
  if (temInstalacaoF) passosF.push('Agendamento da instalação técnica');
  passosF.push(temInstalacaoF && !temMovelF ? 'Instalação concluída e serviço ativado' : temInstalacaoF ? 'Pedido aprovado e iniciamos a ativação dos serviços' : 'Após a aprovação, seguimos com as operações descritas no pedido');
  partes.push('*Próximos passos:*');
  passosF.forEach((p, i) => partes.push(`${i + 1}. ${p}`));
  partes.push('');
  partes.push('Qualquer dúvida estou à disposição!');

  return partes.join('\n');
}
